import {Form, Modal, Row, Col, Select, Table} from "antd";
import {useFormik} from "formik";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import * as Sentry from "@sentry/react";

import {useFetch} from "../../../../../../../hooks/useFetch";
import {useStore} from "../../../../../../../hooks/useStore";
import {getLocaleLang} from "../../../../../../../utils/helpers";
import {PermissionPolicyList} from "../../../permissionPolicyList/PermissionPolicyList";

const UserGroupAssignModal = ({
    userId,
    assignedUserGroups = [],
    visibility,
    onSuccess,
    onCancel
}) => {
    const {t, i18n} = useTranslation(["userAuthorization"]);
    const stores = useStore();
    const [confirmLoading, setConfirmLoading] = useState();

    const [userGroupsLoading, userGroups] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.authorizationStore.listUserGroups.bind(null, {userId}),
        dependencies: [visibility],
        initialCall: true,
        condition: visibility
    });

    const formik = useFormik({
        initialValues: {userId: userId, userGroupKey: ""},
        validationSchema: yup.object().shape({
            userGroupKey: yup.string().required(t("validation.required", {ns: "common"}))
        }),
        enableReinitialize: true,
        onSubmit: async values => {
            setConfirmLoading(true);
            try {
                const assignUserGroupResult = await stores.userStore.assignUserGroup({
                    userId,
                    userGroupKey: values.userGroupKey
                });
                if (!assignUserGroupResult.success) return;

                reset();
                onSuccess();
            } catch (error) {
                Sentry.captureException(error);
                onCancel();
            } finally {
                setConfirmLoading(false);
            }
        }
    });

    const {values, errors, touched, handleSubmit, setFieldValue} = formik;

    const reset = () => {
        setFieldValue("userGroupKey", "");
    };

    return (
        <Modal
            title={t("modals.userGroupAssignModal.assignUserGroup", {ns: "userAuthorization"})}
            visible={visibility}
            onCancel={() => {
                reset();
                onCancel();
            }}
            width={1000}
            onOk={handleSubmit}
            confirmLoading={confirmLoading}
            okText={t("modal.sendButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            <Form labelCol={{span: 24}} wrapperCol={{span: 24}}>
                <Row>
                    <Col span={24}>
                        <Form.Item
                            label={t("common.userGroup", {ns: "userAuthorization"})}
                            validateStatus={
                                errors.userGroupKey && touched.userGroupKey ? "error" : "success"
                            }
                            help={
                                errors.userGroupKey && touched.userGroupKey
                                    ? errors.userGroupKey
                                    : null
                            }
                        >
                            <Select
                                showSearch
                                placeholder={t("modals.userGroupAssignModal.selectUserGroup", {
                                    ns: "userAuthorization"
                                })}
                                optionFilterProp="children"
                                value={values.userGroupKey}
                                onChange={value => setFieldValue("userGroupKey", value)}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                loading={userGroupsLoading}
                            >
                                {userGroups &&
                                    userGroups?.rows?.map(userGroup => {
                                        return (
                                            <Select.Option
                                                key={userGroup.key}
                                                value={userGroup.key}
                                                disabled={assignedUserGroups
                                                    .map(aU => aU.key)
                                                    .includes(userGroup.key)}
                                            >
                                                {userGroup.name[getLocaleLang(i18n)]}
                                            </Select.Option>
                                        );
                                    })}
                            </Select>
                        </Form.Item>
                    </Col>
                    {values.userGroupKey && (
                        <Col span={24}>
                            <h4>{t("common.permissionPolicies", {ns: "userAuthorization"})}</h4>
                            <PermissionPolicyList
                                permissionPolicies={
                                    userGroups?.rows?.find(
                                        userGroup => userGroup.key === values.userGroupKey
                                    )?.permissionPolicies || []
                                }
                            />
                        </Col>
                    )}
                </Row>
            </Form>
        </Modal>
    );
};

export default UserGroupAssignModal;
